import React from "react";
import AiCreditsCard from "../../../../components/Billing/AiCreditsCard";
import { useHistory, useLocation } from "react-router-dom";
import { useBillingContext } from "../../../../contexts/BillingContext";
import CreditCard from "../../../../components/shared-assets/credit-card/credit-card";
import {
  BillingStatusSummary,
  ManageBillingButton,
} from "../../../../components/Billing/BillingActions";
import { SubscriptionPlanPicker } from "../../../../components/Billing/SubscriptionPlanPicker";
import { Button } from "@/components/ui/button";
import { PRODUCT_NAME } from "@/constants/brand";
import { cn } from "@/lib/utils";

function BillingReturnNotice({ notice, onDismiss }: { notice: string | null; onDismiss: () => void }) {
  if (notice === "success") {
    return (
      <div className="relative rounded-md border border-green-200 bg-green-50 p-4 pr-10 dark:border-green-600 dark:bg-green-950/20">
        <p className="mb-1 text-sm font-semibold text-foreground">You&apos;re all set</p>
        <p className="m-0 text-sm text-gray-600 dark:text-gray-400">
          Your subscription or trial should be active shortly. If the dashboard doesn&apos;t unlock,
          refresh in a moment.
        </p>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="absolute right-2 top-2 border-0 bg-transparent p-0 text-lg leading-none text-gray-500 hover:text-gray-700"
        >
          ×
        </button>
      </div>
    );
  }
  if (notice === "canceled") {
    return (
      <div className="relative rounded-md border border-cyan-200 bg-cyan-50 p-4 pr-10 dark:border-cyan-600 dark:bg-cyan-950/20">
        <p className="mb-1 text-sm font-semibold text-foreground">Checkout canceled</p>
        <p className="m-0 text-sm text-gray-600 dark:text-gray-400">
          You can subscribe or start a trial whenever you&apos;re ready.
        </p>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="absolute right-2 top-2 border-0 bg-transparent p-0 text-lg leading-none text-gray-500 hover:text-gray-700"
        >
          ×
        </button>
      </div>
    );
  }
  return null;
}

/** Billing tab under Account Settings — Stripe checkout, portal, and return-flow messaging */
export default function ProfileBillingTab() {
  const location = useLocation();
  const history = useHistory();
  const notice = new URLSearchParams(location.search).get("billingNotice");

  const { status, isLoading, error, isTier1Granted } = useBillingContext();
  const raw = status?.status;
  const s = String(raw ?? "").toLowerCase();
  const showPortal =
    status != null &&
    ["trialing", "active", "past_due", "canceled", "unpaid", "paused", "incomplete"].includes(s);
  const hasStripeCard = Boolean(status?.card_last4);
  const cardNumber = hasStripeCard ? `•••• •••• •••• ${status?.card_last4}` : "•••• •••• •••• ••••";
  const cardHolder = (status?.customer_name || "Card Holder").toUpperCase();
  const cardExpiration =
    status?.card_exp_month && status?.card_exp_year
      ? `${String(status.card_exp_month).padStart(2, "0")}/${String(status.card_exp_year).slice(-2)}`
      : "--/--";
  const cardBrand = (status?.card_brand || "Card").toUpperCase();

  const canSwitchPlan = ["active", "trialing"].includes(s) && Boolean(status?.plan);
  const [showPlans, setShowPlans] = React.useState(false);

  const dismissNotice = () => {
    history.replace("/profile/billing");
  };

  return (
    <div className="flex flex-col gap-6">
      <BillingReturnNotice notice={notice} onDismiss={dismissNotice} />
      {isLoading ? (
        <p className="m-0 text-sm text-gray-500">Loading subscription…</p>
      ) : null}
      {error && !isLoading ? (
        <p className="m-0 text-sm text-red-400">{error}</p>
      ) : null}
      {!isLoading ? <BillingStatusSummary status={status} /> : null}
      {!isLoading ? (
        <div className="w-full max-w-[420px]">
          <CreditCard
            type="brand-dark"
            company={PRODUCT_NAME}
            cardBrand={cardBrand}
            cardNumber={cardNumber}
            cardHolder={cardHolder}
            cardExpiration={cardExpiration}
          />
        </div>
      ) : null}

      <div className={cn("flex flex-col flex-wrap gap-3 sm:flex-row")}>
        {canSwitchPlan || !isTier1Granted ? (
          <Button type="button" variant={canSwitchPlan ? "outline-brand" : "gradient"} data-font="primary"
            className="shadow-none" onClick={() => setShowPlans((v) => !v)} data-change-plan-toggle="">
            <span className="normal-case tracking-normal">
              {showPlans ? "Hide plans" : canSwitchPlan ? "Change plan" : "See plans"}
            </span>
          </Button>
        ) : null}
        {showPortal ? (
          <ManageBillingButton suppressSubscribeFirstToast={isTier1Granted} />
        ) : null}
      </div>

      {(canSwitchPlan || !isTier1Granted) && showPlans ? <SubscriptionPlanPicker className="w-full" /> : null}

      <AiCreditsCard />

      <p className="m-0 text-xs text-gray-500">
        Subscription is billed through Stripe. Change plan and add-ons here; use Manage billing for
        payment method, invoices, and cancellation.
      </p>
    </div>
  );
}
